// 분기 관계 연결선 오버레이 (F9 토글) — "이 슬롯을 고르면 저 분기가 나온다"를 캔버스 문맥에서
// 선으로 보여준다. 슬롯 탭(BranchSlotTabsOverlay의 `[data-slot-tab]`)에서 그 슬롯 children 안의
// 중첩 branch 노드(`[data-composer-node-id]`)로 곡선을 긋는다.
//
// ⚠️ 비대칭 하나가 이 오버레이의 형태를 결정한다: **선은 활성 슬롯에만 그을 수 있다.** 편집
// 캔버스는 활성 슬롯의 children만 렌더하므로(조건 미평가) 비활성 슬롯 속 branch는 DOM에 없고,
// rect가 없으면 선의 도착점이 없다. 반면 "저 안에 분기가 더 있다"는 사실은 template JSON에 있으므로
// 비활성 탭에는 선 대신 ▸ 표식을 붙여 암시한다(branchSlotLinks가 양쪽 childBranchIds를 다 채운다).
// 그래서 이 오버레이는 "활성 경로는 선 · 비활성 가지는 표식"의 두 갈래로 그린다.
//
// ⚠️ 이 오버레이는 **비활성 슬롯 탭 앵커가 화면에 있다**는 전제 위에 선다. 슬롯 탭 바가 전체 슬롯을
// 상시 그리므로(BranchSlotTabBar) 그 전제는 그냥 성립한다 — 한때 활성 슬롯만 담은 칩으로 접어 뒀을
// 때는 비활성 앵커가 아예 없어 F9 동안 강제로 펼쳐야 했다. 배치를 다시 접는 쪽으로 바꾼다면 그
// 결선을 되살릴 것: 없으면 선은 남고 ▸ 표식만 조용히 0개가 된다(에러 없음).
//
// 좌표·측정은 BindingBadgeOverlay/PaddingOverlay 관례를 그대로 따른다: rect 환산은 canvasRect,
// DOM 순회/측정은 **렌더 단계가 아니라** useLayoutEffect(커밋 후) + 시그니처 비교 setState.
// 슬롯 전환·미리보기 resolve 응답 도착 등 부모(EditorPage) 재렌더마다 커밋을 타고 재측정된다.
//
// ⚠️ React.memo 금지(2026-08-31 실사고): memo였을 때 props(canvas/body/activeBranchSlots)가
// 안 바뀌는 재렌더 — 특히 **다회차 슬롯 전환 직후 비동기 미리보기 resolve(previewResult) 도착으로
// 중첩 branch DOM이 뒤늦게 생기는 경우** — 를 걸러내 재측정이 영영 안 돌았다(전환 시점 측정엔
// 대상 rect가 없어 선 0개 → 중첩 슬롯을 한 번 더 바꿔야 나타나는 증상). 탭 오버레이
// (BranchSlotTabsOverlay)와 같은 non-memo + 시그니처 비교 setState 패턴이 정본이다 — 측정은
// 매 커밋 돌지만 시그니처가 같으면 setState가 없어 성능 부담은 동일하다.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ComposerNode } from '@safetyhub/shared';
import { cssEscape, getNodeRect, rectRelativeTo, type Rect } from './canvasRect';
import { branchSlotLinks } from './chainOfBranches';

/** 활성 경로 톤 — 슬롯 탭(teal-600)과 같은 색이라 "이 탭에서 나간 선"으로 읽힌다. */
const LINE_COLOR = '#0d9488';
/** 비활성 가지 표식 톤 — 선보다 한 단계 옅되(teal-500) 흰 바탕에서 읽히는 선까지만. */
const HINT_COLOR = '#14b8a6';

/** 그릴 곡선 1개(측정 완료, 캔버스 상대좌표). */
interface LinkPath {
  key: string;
  /** 출발(슬롯 탭 아래 중앙) */
  x1: number;
  y1: number;
  /** 도착(중첩 branch 노드 위 중앙) */
  x2: number;
  y2: number;
}

/** 비활성 탭에 붙는 ▸ 표식 1개. */
interface NestedHint {
  key: string;
  title: string;
  left: number;
  top: number;
}

/** 탭 아래 중앙점. */
function tabAnchor(r: Rect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height };
}

/** 노드 위 중앙점. */
function nodeAnchor(r: Rect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top };
}

/**
 * 세로 방향 3차 베지어 — 출발/도착 사이 거리에 비례해 제어점을 띄운다(12~60px). 직선이면 여러
 * 선이 겹쳐 어느 탭에서 나갔는지 안 보이고, 과하게 휘면 문서를 가린다.
 */
export function linkPathD(x1: number, y1: number, x2: number, y2: number): string {
  const bow = Math.min(60, Math.max(12, Math.abs(y2 - y1) / 2));
  return `M ${x1} ${y1} C ${x1} ${y1 + bow}, ${x2} ${y2 - bow}, ${x2} ${y2}`;
}

export function BranchLinkOverlay({
  canvas,
  body,
  activeBranchSlots,
}: {
  canvas: HTMLElement | null;
  body: ComposerNode[];
  activeBranchSlots: Record<string, number>;
}) {
  // 연결 대상 파생은 순수(JSON) — DOM은 아래 측정 단계에서만 본다.
  const links = useMemo(
    () => branchSlotLinks(body, activeBranchSlots),
    [body, activeBranchSlots],
  );

  const sigRef = useRef<string>('');
  const [paths, setPaths] = useState<LinkPath[]>([]);
  const [hints, setHints] = useState<NestedHint[]>([]);

  // ⚠️ 측정은 커밋 후(useLayoutEffect), deps 없이 매 커밋마다 수행하고 시그니처가 바뀔 때만
  // setState(BindingBadgeOverlay/PaddingOverlay와 동일 — 렌더 중 측정의 잔상·부하 회피).
  useLayoutEffect(() => {
    if (!canvas) {
      if (sigRef.current !== '') {
        sigRef.current = '';
        setPaths([]);
        setHints([]);
      }
      return;
    }

    const nextPaths: LinkPath[] = [];
    const nextHints: NestedHint[] = [];

    for (const link of links) {
      if (link.childBranchIds.length === 0) continue;
      const tabEl = canvas.querySelector<HTMLElement>(
        `[data-slot-tab="${cssEscape(`${link.branchId}::${link.slotIndex}`)}"]`,
      );
      // 탭이 없으면(체인 바 미표시 등) 출발점도 표식 자리도 없다 — 조용히 건너뛴다.
      if (!tabEl) continue;
      const tabRect = rectRelativeTo(canvas, tabEl);

      if (!link.isActiveSlot) {
        // 비활성 가지 — children이 DOM에 없어 선을 그을 수 없다. 탭 오른쪽에 ▸로 암시만.
        nextHints.push({
          key: `hint-${link.branchId}-${link.slotIndex}`,
          title: `이 슬롯 안에 중첩 분기 ${link.childBranchIds.length}개 — 슬롯을 전환하면 연결선이 보입니다`,
          // 탭 **오른쪽 위 모서리**(위첨자 자리)에 얹는다. 탭 오른쪽 같은 줄에 두면 세그먼트
          // 마지막 슬롯일 때 그 뒤에 오는 ⚠ 배지(missedBranchIds)와 겹친다 — 줄을 반 칸 올려
          // 그 자리를 통째로 피한다.
          left: tabRect.left + tabRect.width - 2,
          top: tabRect.top - 5,
        });
        continue;
      }

      const from = tabAnchor(tabRect);
      for (const childId of link.childBranchIds) {
        const childRect = getNodeRect(canvas, childId);
        if (!childRect) continue; // 렌더 전/조각 부재 — 다음 측정 tick에서 잡힌다.
        const to = nodeAnchor(childRect);
        nextPaths.push({
          key: `link-${link.branchId}-${link.slotIndex}-${childId}`,
          x1: from.x,
          y1: from.y,
          x2: to.x,
          y2: to.y,
        });
      }
    }

    const sig = JSON.stringify([nextPaths, nextHints]);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setPaths(nextPaths);
      setHints(nextHints);
    }
  }); // deps 없음 — 매 커밋(재측정 tick 포함) 후 최신 DOM 측정

  if (!canvas || (paths.length === 0 && hints.length === 0)) return null;

  // ⚠️ 두 층을 **형제로** 내보낸다(한 컨테이너 안에 중첩 금지). zIndex를 가진 absolute 박스는
  // 자체 stacking context를 만들어, 그 **안**의 zIndex는 바깥 형제(탭 오버레이 zIndex 26)와
  // 겨루지 못한다 — 감싸면 표식이 영영 탭 아래로 깔린다.
  return (
    <>
      {/* 선 층 — 탭(zIndex 26)보다 아래라 탭을 가리거나 클릭을 가로채지 않는다. */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 25 }}>
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
          aria-hidden={true}
        >
          <defs>
            <marker
              id="branch-link-arrow"
              viewBox="0 0 8 8"
              refX="4"
              refY="4"
              markerWidth="5"
              markerHeight="5"
              orient="auto-start-reverse"
            >
              <path d="M 0 1 L 6 4 L 0 7 z" fill={LINE_COLOR} />
            </marker>
          </defs>
          {paths.map(({ key, x1, y1, x2, y2 }) => (
            <path
              key={key}
              d={linkPathD(x1, y1, x2, y2)}
              fill="none"
              stroke={LINE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              markerEnd="url(#branch-link-arrow)"
              opacity={0.85}
            />
          ))}
        </svg>
      </div>
      {/* 표식 층 — 선을 그을 수 없는 비활성 슬롯에 "안에 분기가 더 있다"만 알린다. 탭 모서리에
          얹으므로 탭(26)보다 **위**여야 이웃 탭에 가려지지 않는다. pointer-events는 none이라
          탭 클릭은 그대로 통과한다. */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 27 }}>
        {hints.map(({ key, title, left, top }) => (
          <span
            key={key}
            title={title}
            style={{
              position: 'absolute',
              left,
              top,
              fontSize: 9,
              lineHeight: '9px',
              color: HINT_COLOR,
              userSelect: 'none',
            }}
          >
            ▸
          </span>
        ))}
      </div>
    </>
  );
}
